import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPackagings,
  fetchProductsByPackaging,
  savePackagingResearch,
  publishClientResearch,
} from '@/api/business/marketResearch';
import { SavePackagingResearchPayload } from '@/types/MarketResearch';
import { ResearchedProductKeys } from './useResearchedProductQuery';

export const PackagingResearchKeys = {
  packagings: (skPesquisa: number, skCliente: number) => [
    'packagings',
    skPesquisa,
    skCliente,
  ] as const,
  productsByPackaging: (
    skPesquisa: number,
    skCliente: number,
    ddSku: string
  ) => ['products-by-packaging', skPesquisa, skCliente, ddSku] as const,
};

export const usePackagingsQuery = (
  skPesquisa: number,
  skCliente: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: PackagingResearchKeys.packagings(skPesquisa, skCliente),
    queryFn: () => fetchPackagings(skPesquisa, skCliente),
    enabled: enabled && Boolean(skPesquisa) && Boolean(skCliente),
  });
};

export const useProductsByPackagingQuery = (
  skPesquisa: number,
  skCliente: number,
  ddSku: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: PackagingResearchKeys.productsByPackaging(
      skPesquisa,
      skCliente,
      ddSku
    ),
    queryFn: () => fetchProductsByPackaging(skPesquisa, skCliente, ddSku),
    enabled: enabled && Boolean(skPesquisa) && Boolean(skCliente) && Boolean(ddSku),
  });
};

export const useSavePackagingResearchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SavePackagingResearchPayload) => savePackagingResearch(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: PackagingResearchKeys.packagings(
          variables.SK_PESQUISA,
          variables.SK_CLIENTE
        ),
      });
      queryClient.invalidateQueries({
        queryKey: PackagingResearchKeys.productsByPackaging(
          variables.SK_PESQUISA,
          variables.SK_CLIENTE,
          variables.DD_SKU
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ResearchedProductKeys.details(),
      });
    },
  });
};

export const usePublishClientResearchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      skPesquisa,
      skCliente,
    }: {
      skPesquisa: number;
      skCliente: number;
    }) => publishClientResearch(skPesquisa, skCliente),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: PackagingResearchKeys.packagings(
          variables.skPesquisa,
          variables.skCliente
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ResearchedProductKeys.details(),
      });
    },
  });
};
